/**
 * Offline Review Queue Page
 * 
 * Manager-facing review screen for pending, failed, and conflict sync items.
 * Provides retry, discard, and annotation capabilities.
 */

import { useState } from 'react';
import { 
  Cloud, 
  CloudOff, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle, 
  XCircle,
  Clock,
  Filter,
  Trash2,
  RotateCcw,
  MessageSquare,
  ChevronDown,
  ChevronRight,
  Eye,
  FileText,
  Settings,
  Users,
  UtensilsCrossed,
  Package,
  Tag
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { useOfflineReviewQueue, REVIEW_STATUS } from '@/hooks/useOfflineReviewQueue';
import { useOfflineSync } from '@/hooks/useOfflineSync';

// Module icons
const MODULE_ICONS = {
  Settings: Settings,
  Staff: Users,
  Menu: UtensilsCrossed,
  Inventory: Package,
  Discounts: Tag,
  Kitchen: Settings,
  Payroll: FileText,
  Other: FileText
};

// Status colors and icons
const STATUS_CONFIG = {
  pending: {
    icon: Clock,
    color: 'text-blue-500',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    label: 'Pending'
  },
  syncing: {
    icon: RefreshCw,
    color: 'text-blue-500',
    bg: 'bg-blue-100 dark:bg-blue-900/30',
    label: 'Syncing',
    animate: true
  },
  completed: {
    icon: CheckCircle,
    color: 'text-green-500',
    bg: 'bg-green-100 dark:bg-green-900/30',
    label: 'Synced'
  },
  failed: {
    icon: XCircle,
    color: 'text-red-500',
    bg: 'bg-red-100 dark:bg-red-900/30',
    label: 'Failed'
  },
  conflict: {
    icon: AlertCircle,
    color: 'text-purple-500',
    bg: 'bg-purple-100 dark:bg-purple-900/30',
    label: 'Conflict'
  }
};

/**
 * Queue Item Card Component
 */
function QueueItemCard({ item, onRetry, onDiscard, onAddNote, onMarkReviewed, onViewDetails }) {
  const [expanded, setExpanded] = useState(false);
  const statusConfig = STATUS_CONFIG[item.status] || STATUS_CONFIG.pending;
  const StatusIcon = statusConfig.icon;
  const ModuleIcon = MODULE_ICONS[item.moduleName] || MODULE_ICONS.Other;
  
  const formatTime = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const date = new Date(timestamp);
    return date.toLocaleString();
  };

  return (
    <Card className="mb-3">
      <CardContent className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-3">
            <div className={cn("p-2 rounded-lg", statusConfig.bg)}>
              <ModuleIcon className={cn("h-5 w-5", statusConfig.color)} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{item.actionDisplayName || item.type}</span>
                <Badge variant="outline" className={cn("text-xs", statusConfig.color)}>
                  <StatusIcon className={cn("h-3 w-3 mr-1", statusConfig.animate && "animate-spin")} />
                  {statusConfig.label}
                </Badge>
                {item.manualResolutionStatus && item.manualResolutionStatus !== 'none' && (
                  <Badge variant="secondary" className="text-xs">
                    {item.manualResolutionStatus}
                  </Badge>
                )}
              </div>
              <p className="text-sm text-muted-foreground mt-1">
                {item.moduleName} • {formatTime(item.createdAt)}
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setExpanded(!expanded)}
            >
              {expanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
            </Button>
            
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="outline" size="sm">Actions</Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {(item.status === 'failed' || item.status === 'conflict') && (
                  <DropdownMenuItem onClick={() => onRetry(item.id)}>
                    <RotateCcw className="h-4 w-4 mr-2" />
                    Retry Now
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={() => onViewDetails(item)}>
                  <Eye className="h-4 w-4 mr-2" />
                  View Details
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => onAddNote(item)}>
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Add Note
                </DropdownMenuItem>
                {item.status !== 'completed' && (
                  <DropdownMenuItem onClick={() => onMarkReviewed(item.id)}>
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark Reviewed
                  </DropdownMenuItem>
                )}
                <DropdownMenuSeparator />
                <DropdownMenuItem 
                  onClick={() => onDiscard(item.id)}
                  className="text-red-600"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Discard
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
        
        {/* Expanded details */}
        {expanded && (
          <div className="mt-4 pt-4 border-t space-y-3">
            <div className="grid grid-cols-2 gap-4 text-sm">
              <div>
                <span className="text-muted-foreground">Entity Type:</span>
                <span className="ml-2">{item.entityType || 'N/A'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Entity ID:</span>
                <span className="ml-2 font-mono text-xs">{item.entityId || 'N/A'}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Retry Count:</span>
                <span className="ml-2">{item.retryCount || 0}</span>
              </div>
              <div>
                <span className="text-muted-foreground">Updated:</span>
                <span className="ml-2">{formatTime(item.updatedAt)}</span>
              </div>
            </div>
            
            {item.lastError && (
              <div className="p-2 rounded bg-red-50 dark:bg-red-900/20 text-sm">
                <span className="font-medium text-red-700 dark:text-red-400">Error: </span>
                <span className="text-red-600 dark:text-red-300">{item.lastError}</span>
              </div>
            )}
            
            {item.managerNote && (
              <div className="p-2 rounded bg-blue-50 dark:bg-blue-900/20 text-sm">
                <span className="font-medium text-blue-700 dark:text-blue-400">Manager Note: </span>
                <span className="text-blue-600 dark:text-blue-300">{item.managerNote}</span>
                {item.noteAddedBy && (
                  <span className="text-xs text-muted-foreground ml-2">
                    - {item.noteAddedBy}
                  </span>
                )}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
}

/**
 * Summary Stats Widget
 */
function SummaryStats({ counts, onSyncNow, onClearCompleted, isOnline, isSyncing }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6">
      <Card>
        <CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-blue-600">{counts.pending}</p>
          <p className="text-sm text-muted-foreground">Pending</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-red-600">{counts.failed}</p>
          <p className="text-sm text-muted-foreground">Failed</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-purple-600">{counts.conflict}</p>
          <p className="text-sm text-muted-foreground">Conflicts</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-amber-600">{counts.blocked}</p>
          <p className="text-sm text-muted-foreground">Needs Review</p>
        </CardContent>
      </Card>
      <Card>
        <CardContent className="p-4 text-center">
          <p className="text-3xl font-bold text-green-600">{counts.synced}</p>
          <p className="text-sm text-muted-foreground">Synced</p>
        </CardContent>
      </Card>
    </div>
  );
}

/**
 * Main Review Queue Page
 */
export default function OfflineReviewPage() {
  const { isOnline, isSyncing } = useOfflineSync();
  const {
    pendingItems,
    failedItems,
    conflictItems,
    blockedItems,
    recentSyncedItems,
    summaryCounts,
    availableModules,
    loading,
    filters,
    setFilters,
    retryItem,
    discardItem,
    addNote,
    markReviewed,
    syncNow,
    clearCompleted,
    retryAllFailed,
    refreshData
  } = useOfflineReviewQueue();

  const [activeTab, setActiveTab] = useState('pending');
  const [noteDialog, setNoteDialog] = useState({ open: false, item: null });
  const [detailsDialog, setDetailsDialog] = useState({ open: false, item: null });
  const [noteText, setNoteText] = useState('');

  // Handle retry
  const handleRetry = async (itemId) => {
    const result = await retryItem(itemId);
    if (result.success) {
      toast.success('Item queued for retry');
    } else {
      toast.error(`Retry failed: ${result.error}`);
    }
  };

  // Handle discard
  const handleDiscard = async (itemId) => {
    const result = await discardItem(itemId);
    if (result.success) {
      toast.success('Item discarded');
    } else {
      toast.error(`Discard failed: ${result.error}`);
    }
  };

  // Handle add note
  const handleOpenNoteDialog = (item) => {
    setNoteText(item.managerNote || '');
    setNoteDialog({ open: true, item });
  };

  const handleSaveNote = async () => {
    if (!noteDialog.item) return;
    const result = await addNote(noteDialog.item.id, noteText, 'Manager');
    if (result.success) {
      toast.success('Note saved');
      setNoteDialog({ open: false, item: null });
    } else {
      toast.error(`Failed to save note: ${result.error}`);
    }
  };

  // Handle mark reviewed
  const handleMarkReviewed = async (itemId) => {
    const result = await markReviewed(itemId, 'Manager');
    if (result.success) {
      toast.success('Marked as reviewed');
    } else {
      toast.error(`Failed: ${result.error}`);
    }
  };

  // Handle view details
  const handleViewDetails = (item) => {
    setDetailsDialog({ open: true, item });
  };

  // Handle sync now
  const handleSyncNow = async () => {
    const result = await syncNow();
    if (result.success) {
      toast.success('Sync started');
    } else {
      toast.error(`Sync failed: ${result.error}`);
    }
  };

  // Handle retry all failed
  const handleRetryAllFailed = async () => {
    const result = await retryAllFailed();
    if (result.success) {
      toast.success(`${result.count} items queued for retry`);
    } else {
      toast.error(`Failed: ${result.error}`);
    }
  };

  // Get items for current tab
  const getCurrentItems = () => {
    switch (activeTab) {
      case 'pending': return pendingItems;
      case 'failed': return failedItems;
      case 'conflicts': return conflictItems;
      case 'review': return blockedItems;
      case 'synced': return recentSyncedItems;
      default: return pendingItems;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <img src="/chain29-logo.png" alt="Loading" className="w-12 h-12 rounded-xl object-cover animate-pulse" />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="offline-review-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            {isOnline ? (
              <Cloud className="h-6 w-6 text-green-500" />
            ) : (
              <CloudOff className="h-6 w-6 text-amber-500" />
            )}
            Offline Sync Review
          </h1>
          <p className="text-muted-foreground mt-1">
            Review and manage pending, failed, and conflict sync items
          </p>
        </div>
        
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={refreshData}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Button 
            onClick={handleSyncNow}
            disabled={!isOnline || isSyncing || summaryCounts.pending === 0}
          >
            {isSyncing ? (
              <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
            ) : (
              <Cloud className="h-4 w-4 mr-2" />
            )}
            Sync Now
          </Button>
        </div>
      </div>

      {/* Summary Stats */}
      <SummaryStats 
        counts={summaryCounts}
        onSyncNow={handleSyncNow}
        onClearCompleted={clearCompleted}
        isOnline={isOnline}
        isSyncing={isSyncing}
      />

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Filter className="h-4 w-4 text-muted-foreground" />
              <span className="text-sm font-medium">Filters:</span>
            </div>
            
            <Select 
              value={filters.module || 'all'} 
              onValueChange={(v) => setFilters(f => ({ ...f, module: v === 'all' ? null : v }))}
            >
              <SelectTrigger className="w-40">
                <SelectValue placeholder="Module" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Modules</SelectItem>
                {availableModules.map(mod => (
                  <SelectItem key={mod} value={mod}>{mod}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {filters.module && (
              <Button 
                variant="ghost" 
                size="sm"
                onClick={() => setFilters(f => ({ ...f, module: null }))}
              >
                Clear Filters
              </Button>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid grid-cols-5 w-full max-w-2xl">
          <TabsTrigger value="pending" className="relative">
            Pending
            {pendingItems.length > 0 && (
              <Badge className="ml-2 h-5 px-1.5" variant="secondary">
                {pendingItems.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="failed" className="relative">
            Failed
            {failedItems.length > 0 && (
              <Badge className="ml-2 h-5 px-1.5 bg-red-500">
                {failedItems.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="conflicts">
            Conflicts
            {conflictItems.length > 0 && (
              <Badge className="ml-2 h-5 px-1.5 bg-purple-500">
                {conflictItems.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="review">
            Needs Review
            {blockedItems.length > 0 && (
              <Badge className="ml-2 h-5 px-1.5 bg-amber-500">
                {blockedItems.length}
              </Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="synced">
            Synced
          </TabsTrigger>
        </TabsList>

        <div className="mt-4">
          {/* Bulk actions for failed tab */}
          {activeTab === 'failed' && failedItems.length > 0 && (
            <div className="mb-4 flex items-center gap-2">
              <Button 
                variant="outline" 
                size="sm"
                onClick={handleRetryAllFailed}
                disabled={!isOnline}
              >
                <RotateCcw className="h-4 w-4 mr-2" />
                Retry All Failed ({failedItems.length})
              </Button>
            </div>
          )}

          {/* Item list */}
          <ScrollArea className="h-[500px]">
            {getCurrentItems().length === 0 ? (
              <div className="text-center py-12 text-muted-foreground">
                <CheckCircle className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No items in this category</p>
              </div>
            ) : (
              getCurrentItems().map(item => (
                <QueueItemCard
                  key={item.id}
                  item={item}
                  onRetry={handleRetry}
                  onDiscard={handleDiscard}
                  onAddNote={handleOpenNoteDialog}
                  onMarkReviewed={handleMarkReviewed}
                  onViewDetails={handleViewDetails}
                />
              ))
            )}
          </ScrollArea>
        </div>
      </Tabs>

      {/* Note Dialog */}
      <Dialog open={noteDialog.open} onOpenChange={(open) => !open && setNoteDialog({ open: false, item: null })}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Manager Note</DialogTitle>
            <DialogDescription>
              Add a note to this sync item for tracking and follow-up.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              placeholder="Enter your note..."
              value={noteText}
              onChange={(e) => setNoteText(e.target.value)}
              rows={4}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setNoteDialog({ open: false, item: null })}>
              Cancel
            </Button>
            <Button onClick={handleSaveNote}>Save Note</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Details Dialog */}
      <Dialog open={detailsDialog.open} onOpenChange={(open) => !open && setDetailsDialog({ open: false, item: null })}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>Item Details</DialogTitle>
          </DialogHeader>
          {detailsDialog.item && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4 text-sm">
                <div>
                  <Label className="text-muted-foreground">Action Type</Label>
                  <p className="font-medium">{detailsDialog.item.actionDisplayName}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Status</Label>
                  <p className="font-medium">{detailsDialog.item.status}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Module</Label>
                  <p className="font-medium">{detailsDialog.item.moduleName}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Retry Count</Label>
                  <p className="font-medium">{detailsDialog.item.retryCount || 0}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Created</Label>
                  <p className="font-medium">{new Date(detailsDialog.item.createdAt).toLocaleString()}</p>
                </div>
                <div>
                  <Label className="text-muted-foreground">Idempotency Key</Label>
                  <p className="font-mono text-xs truncate">{detailsDialog.item.idempotencyKey}</p>
                </div>
              </div>
              
              {detailsDialog.item.lastError && (
                <div>
                  <Label className="text-muted-foreground">Last Error</Label>
                  <p className="mt-1 p-2 rounded bg-red-50 dark:bg-red-900/20 text-red-700 dark:text-red-300 text-sm">
                    {detailsDialog.item.lastError}
                  </p>
                </div>
              )}
              
              <div>
                <Label className="text-muted-foreground">Payload</Label>
                <pre className="mt-1 p-2 rounded bg-muted text-xs overflow-auto max-h-40">
                  {JSON.stringify(detailsDialog.item.payload, null, 2)}
                </pre>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailsDialog({ open: false, item: null })}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
